import { useState, useEffect, useCallback } from 'react';

/**
 * Intersection observer with a layout fallback so content is not left at opacity:0
 * when the observer misses the first paint (common on public gallery pages).
 */
export function useInView(threshold = 0.08) {
  const [element, setElement] = useState(null);
  const [inView, setInView] = useState(false);

  const setRef = useCallback((node) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (!element) return undefined;

    const reveal = () => setInView(true);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) reveal();
      },
      { threshold, rootMargin: '120px 0px 80px 0px' },
    );

    observer.observe(element);

    const checkNow = () => {
      const rect = element.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (rect.width > 0 && rect.top < vh + 120 && rect.bottom > -80) {
        reveal();
      }
    };

    const raf = requestAnimationFrame(checkNow);
    const fallback = window.setTimeout(reveal, 500);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(fallback);
      observer.disconnect();
    };
  }, [element, threshold]);

  return [setRef, inView];
}
